import React, { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { Drawer, Modal, VbButton, Input, Textarea, Tabs, type Big5Category } from "@/components/vb";
import type { Buyer, Question } from "@/types/vb";
import { LocationField } from "../buyer-create/LocationField";
import { CompetitorEditor } from "../buyer-create/CompetitorEditor";
import { QuestionList } from "../buyer-create/QuestionList";
import {
  competitorUrlKey,
  domainToCompetitor,
  describeDescriptionLength,
  MAX_COMPETITORS,
  MAX_DESCRIPTION,
  MAX_MARKET_CITY,
  MAX_NAME,
  MAX_PER_CATEGORY,
  MAX_QUESTIONS,
  MIN_DESCRIPTION,
  type CompetitorDraft,
} from "../buyer-create/useBuyerWizard";
import { extractVbError } from "../buyer-create/errors";
import {
  useAddCompetitor,
  useAddQuestion,
  useDeleteCompetitor,
  useDeleteQuestion,
  useGenerateQuestions,
  usePatchBuyer,
  useUpdateQuestion,
} from "@/hooks/vb/useBuyers";

/** Drawer question order (prototype: Cost, Problems, Head-to-Head, Reviews, Best Of). */
const DRAWER_CATEGORIES: Big5Category[] = ["cost", "problems", "head_to_head", "reviews", "best_of"];

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  buyer: Buyer;
  /** Current questions (from useQuestions; refetched on mutation via invalidation). */
  questions: Question[];
};

/** VB-G · edit a buyer + its questions in a side drawer, tabbed Buyer details /
 *  Questions. Persona scalar fields batch into "Save changes"; competitors and
 *  questions persist immediately (live CRUD). No full-page navigation. */
export function EditBuyerDrawer({ open, onOpenChange, buyer, questions }: Props) {
  const buyerId = buyer.id;
  const [tab, setTab] = useState<"buyer" | "questions">("buyer");

  // Persona scalars (batched). Seeded from the buyer each time the drawer opens.
  const [name, setName] = useState(buyer.name);
  const [description, setDescription] = useState(buyer.description);
  const [region, setRegion] = useState(buyer.market_region);
  const [city, setCity] = useState(buyer.market_city);

  // Competitors are live CRUD; local list (with server ids) seeded on open.
  const [competitors, setCompetitors] = useState<CompetitorDraft[]>([]);

  const [personaError, setPersonaError] = useState<string | null>(null);
  const [competitorError, setCompetitorError] = useState<string | null>(null);
  const [questionError, setQuestionError] = useState<string | null>(null);
  const [regenOpen, setRegenOpen] = useState(false);

  const patchM = usePatchBuyer();
  const addComp = useAddCompetitor();
  const deleteComp = useDeleteCompetitor();
  const addQ = useAddQuestion();
  const updateQ = useUpdateQuestion();
  const deleteQ = useDeleteQuestion();
  const generateM = useGenerateQuestions();

  // Re-seed local form state when the drawer opens (not on every buyer refetch,
  // so a live competitor add doesn't clobber what the user is typing).
  useEffect(() => {
    if (!open) return;
    setTab("buyer");
    setName(buyer.name);
    setDescription(buyer.description);
    setRegion(buyer.market_region);
    setCity(buyer.market_city);
    setCompetitors(buyer.competitors.map((c) => ({ id: c.id, name: c.name, url: c.url })));
    setPersonaError(null);
    setCompetitorError(null);
    setQuestionError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Same bounds as the create wizard — the PATCH hits the same serializer.
  const personaComplete =
    name.trim().length > 0 &&
    name.trim().length <= MAX_NAME &&
    description.trim().length >= MIN_DESCRIPTION &&
    description.trim().length <= MAX_DESCRIPTION &&
    region.trim().length > 0 &&
    city.trim().length <= MAX_MARKET_CITY;

  // --- Competitors (live CRUD) ----------------------------------------------
  const addCompetitor = async (raw: string) => {
    setCompetitorError(null);
    let draft: CompetitorDraft | null;
    try {
      draft = await domainToCompetitor(raw);
    } catch {
      setCompetitorError("Couldn't check that domain just now. Please try again.");
      return;
    }
    if (!draft) {
      setCompetitorError("Enter a valid domain, e.g. competitor.com");
      return;
    }
    if (competitors.length >= MAX_COMPETITORS) {
      setCompetitorError(`Up to ${MAX_COMPETITORS} competitors.`);
      return;
    }
    if (competitors.some((c) => competitorUrlKey(c.url) === competitorUrlKey(draft!.url))) {
      setCompetitorError("That competitor is already added.");
      return;
    }
    try {
      const saved = await addComp.mutateAsync({ buyerId, competitor: draft });
      setCompetitors((cs) => [...cs, { id: saved.id, name: saved.name, url: saved.url }]);
    } catch (err) {
      const e = extractVbError(err);
      setCompetitorError(
        e.code === "DUPLICATE_URL"
          ? "That competitor is already added."
          : e.code === "TOTAL_CAP_REACHED"
            ? `Up to ${MAX_COMPETITORS} competitors.`
            : e.detail,
      );
    }
  };

  const removeCompetitor = async (target: CompetitorDraft) => {
    setCompetitorError(null);
    if (target.id != null) {
      try {
        await deleteComp.mutateAsync(target.id);
      } catch (err) {
        setCompetitorError(extractVbError(err).detail);
        return;
      }
    }
    setCompetitors((cs) => cs.filter((c) => c.url !== target.url));
  };

  // --- Questions (live CRUD; list refetched via invalidation) ---------------
  const perCategoryCount = (category: Big5Category) =>
    questions.filter((q) => q.category === category).length;

  const addManualQuestion = async (category: Big5Category, text: string) => {
    setQuestionError(null);
    const trimmed = text.trim();
    if (!trimmed) return;
    if (questions.length >= MAX_QUESTIONS) {
      setQuestionError(`Up to ${MAX_QUESTIONS} questions total. Remove one first.`);
      return;
    }
    if (perCategoryCount(category) >= MAX_PER_CATEGORY) {
      setQuestionError(`Up to ${MAX_PER_CATEGORY} questions per topic. Remove one first.`);
      return;
    }
    try {
      await addQ.mutateAsync({ buyerId, category, text: trimmed });
    } catch (err) {
      const e = extractVbError(err);
      setQuestionError(
        e.code === "CATEGORY_CAP_REACHED"
          ? `Up to ${MAX_PER_CATEGORY} questions per topic. Remove one first.`
          : e.code === "TOTAL_CAP_REACHED"
            ? `Up to ${MAX_QUESTIONS} questions total. Remove one first.`
            : e.detail,
      );
    }
  };

  const editQuestion = async (questionId: string, text: string) => {
    setQuestionError(null);
    const trimmed = text.trim();
    if (!trimmed) return;
    try {
      await updateQ.mutateAsync({ questionId, text: trimmed });
    } catch (err) {
      setQuestionError(extractVbError(err).detail);
    }
  };

  const removeQuestion = async (questionId: string) => {
    setQuestionError(null);
    try {
      await deleteQ.mutateAsync(questionId);
    } catch (err) {
      setQuestionError(extractVbError(err).detail);
    }
  };

  // Regenerate is destructive (replace-all), so it goes through an in-app
  // confirm Modal rather than a native window.confirm.
  const doRegenerate = async () => {
    setRegenOpen(false);
    if (generateM.isPending) return;
    setQuestionError(null);
    try {
      await generateM.mutateAsync(buyerId);
    } catch (err) {
      setQuestionError(extractVbError(err).detail);
    }
  };

  // --- Save / cancel --------------------------------------------------------
  const save = async () => {
    if (!personaComplete || patchM.isPending) return;
    setPersonaError(null);
    try {
      await patchM.mutateAsync({
        buyerId,
        partial: {
          name: name.trim(),
          description: description.trim(),
          market_region: region.trim(),
          market_city: city.trim(),
        },
      });
      onOpenChange(false);
    } catch (err) {
      const e = extractVbError(err);
      setPersonaError(
        e.code === "DUPLICATE_NAME"
          ? "You already have a buyer with this name for this company."
          : e.detail,
      );
    }
  };

  const descHint = describeDescriptionLength(description.trim().length);

  const footer = (
    <>
      {/* "Close" not "Cancel": competitors + questions persist immediately
          (live CRUD), so only unsaved persona-field edits are discarded here. */}
      <VbButton type="button" variant="secondary" onClick={() => onOpenChange(false)}>
        Close
      </VbButton>
      <VbButton type="button" onClick={() => void save()} disabled={!personaComplete || patchM.isPending}>
        {patchM.isPending ? "Saving…" : "Save changes"}
      </VbButton>
    </>
  );

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      eyebrow="Edit buyer"
      title={buyer.name}
      footer={footer}
    >
      {/* The shared Tabs primitive (neutral-ink underline), now a fully-wired
          ARIA tablist + arrow keys controlling the panel below — the wiring the
          old hand-rolled toggle buttons deliberately skipped. */}
      <Tabs
        variant="underline"
        tone="ink"
        idBase="ebd-tab"
        panelId="ebd-panel"
        ariaLabel="Edit buyer sections"
        value={tab}
        onChange={(v) => setTab(v as "buyer" | "questions")}
        items={[
          { value: "buyer", label: "Buyer details" },
          { value: "questions", label: "Questions", count: questions.length },
        ]}
      />

      {tab === "buyer" ? (
        <div
          role="tabpanel"
          id="ebd-panel"
          aria-labelledby="ebd-tab-buyer"
          tabIndex={0}
          className="field-panel active vb-drawer-form"
        >
          {personaError && (
            <div className="limit-flash show" role="alert">{personaError}</div>
          )}

          <div className="pf-input">
            <span className="pf-label">Name</span>
            <Input
              size="lg"
              type="text"
              value={name}
              maxLength={MAX_NAME}
              onChange={(e) => setName(e.target.value)}
              aria-label="Buyer name"
            />
          </div>

          <div className="pf-input">
            <span className="pf-label">Who they are</span>
            <Textarea
              size="lg"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              aria-label="Buyer description"
            />
            <span className={`help${descHint.state === "over" ? " over-limit" : ""}`}>
              The persona AI answers as. {descHint.text}
            </span>
          </div>

          <LocationField
            region={region}
            city={city}
            onChange={({ region: r, city: c }) => {
              setRegion(r);
              setCity(c);
            }}
          />

          <div className="pf-input">
            <span className="pf-label">Competitors</span>
            <CompetitorEditor
              competitors={competitors}
              onAdd={(raw) => void addCompetitor(raw)}
              onRemove={(c) => void removeCompetitor(c)}
              error={competitorError}
              busy={addComp.isPending || deleteComp.isPending}
            />
          </div>
        </div>
      ) : (
        <div
          role="tabpanel"
          id="ebd-panel"
          aria-labelledby="ebd-tab-questions"
          tabIndex={0}
          className="vb-drawer-questions"
        >
          <div className="vb-drawer-q-top">
            <span className="vb-eyebrow">Big-5 questions · up to 2 per topic</span>
            <VbButton type="button" variant="secondary" onClick={() => setRegenOpen(true)} disabled={generateM.isPending}>
              <RefreshCw size={14} aria-hidden="true" />
              {generateM.isPending ? "Regenerating…" : "Regenerate"}
            </VbButton>
          </div>

          {questionError && (
            <div className="limit-flash show" role="alert">{questionError}</div>
          )}

          {DRAWER_CATEGORIES.map((category) => (
            <QuestionList
              key={category}
              category={category}
              questions={questions.filter((q) => q.category === category)}
              onEdit={(id, text) => void editQuestion(id, text)}
              onRemove={(id) => void removeQuestion(id)}
              onAdd={(text) => void addManualQuestion(category, text)}
              busy={addQ.isPending || generateM.isPending}
            />
          ))}
        </div>
      )}

      <Modal
        open={regenOpen}
        onOpenChange={setRegenOpen}
        title="Regenerate questions?"
        description={`This replaces all ${questions.length} questions with a fresh Big-5 set, including any you edited or added.`}
        footer={
          <>
            <VbButton type="button" variant="secondary" onClick={() => setRegenOpen(false)}>
              Cancel
            </VbButton>
            <VbButton type="button" onClick={() => void doRegenerate()}>
              Regenerate
            </VbButton>
          </>
        }
      />
    </Drawer>
  );
}

export default EditBuyerDrawer;
